// Yeni Müşteri — plan §4.3.3 + design ref #screen-client-new.
// 3-adımlı wizard: (1) Şirket bilgileri (2) İletişim kişisi (opsiyonel) (3) Onay.

import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SECTOR_CODES, SECTOR_LABELS, type Client } from "@mahir/shared";
import { api, ApiError } from "../api/client";

type Step = 1 | 2 | 3;

interface FormData {
  name: string;
  sector: string;
  country: string;
  size: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
}

const EMPTY: FormData = {
  name: "",
  sector: "",
  country: "",
  size: "",
  primaryContactName: "",
  primaryContactEmail: "",
  primaryContactPhone: "",
};

export function ClientNew(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step1Valid = form.name.trim().length >= 2;

  async function submit(e?: FormEvent, thenCreateProject = false) {
    if (e) e.preventDefault();
    if (!step1Valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        sector: form.sector.trim() || undefined,
        country: form.country.trim() || undefined,
        size: form.size.trim() || undefined,
        primaryContactName: form.primaryContactName.trim() || undefined,
        primaryContactEmail: form.primaryContactEmail.trim() || undefined,
        primaryContactPhone: form.primaryContactPhone.trim() || undefined,
      };
      const res = await api<{ client: Client }>("/clients", { method: "POST", body });
      if (thenCreateProject) {
        void navigate(`/projects/new?clientId=${res.client.id}`);
      } else {
        void navigate(`/clients/${res.client.id}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kaydedilemedi");
    } finally {
      setSubmitting(false);
    }
  }

  function update<K extends keyof FormData>(key: K, value: FormData[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 720 }}>
      <Link to="/clients" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Müşteri listesine dön
      </Link>
      <h1
        className="mt-4 text-3xl"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Yeni Müşteri
      </h1>
      <p className="text-sm mt-1 mb-6" style={{ color: "var(--color-text-2)" }}>
        3 basit adımda yeni müşteri ekleyin.
      </p>

      <Stepper step={step} />

      {error ? (
        <div
          role="alert"
          className="mb-4 px-3 py-2 rounded-lg text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      ) : null}

      {step === 1 ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (step1Valid) setStep(2);
          }}
          className="grid gap-4"
        >
          <Field
            label="Şirket Adı *"
            value={form.name}
            onChange={(v) => update("name", v)}
            minLength={2}
            required
            autoFocus
            placeholder="ör. Sefer Sürdürülebilirlik A.Ş."
          />
          <div className="grid grid-cols-2 gap-4">
            {/* Serbest metin KALIR; datalist yalnız sektör sözlüğünü önerir —
                sektör paketi önerisi bu değeri `normalizeSectorCode` ile okur. */}
            <Field
              label="Sektör"
              value={form.sector}
              onChange={(v) => update("sector", v)}
              placeholder="ör. Enerji"
              listId="sektor-sozlugu"
              listOptions={SECTOR_CODES.map((c) => SECTOR_LABELS[c])}
            />
            <Field
              label="Ülke"
              value={form.country}
              onChange={(v) => update("country", v)}
              placeholder="ör. Türkiye"
            />
          </div>
          <Field
            label="Büyüklük"
            value={form.size}
            onChange={(v) => update("size", v)}
            placeholder="ör. 500+ çalışan"
          />
          <Nav
            canBack={false}
            canNext={step1Valid}
            onNext={() => setStep(2)}
            onCancel={() => void navigate("/clients")}
          />
        </form>
      ) : null}

      {step === 2 ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setStep(3);
          }}
          className="grid gap-4"
        >
          <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
            İletişim kişisi opsiyoneldir — boş bırakıp atlayabilirsiniz.
          </p>
          <Field
            label="Ad Soyad"
            value={form.primaryContactName}
            onChange={(v) => update("primaryContactName", v)}
            placeholder="ör. Ayşe Kaya"
          />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="E-posta"
              value={form.primaryContactEmail}
              onChange={(v) => update("primaryContactEmail", v)}
              type="email"
              placeholder="ayse@sirket.com"
            />
            <Field
              label="Telefon"
              value={form.primaryContactPhone}
              onChange={(v) => update("primaryContactPhone", v)}
              placeholder="+90 5xx xxx xx xx"
            />
          </div>
          <Nav
            canBack
            canNext
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            onCancel={() => void navigate("/clients")}
          />
        </form>
      ) : null}

      {step === 3 ? (
        <div>
          <SummaryCard form={form} />
          <Nav
            canBack
            canNext={false}
            onBack={() => setStep(2)}
            onCancel={() => void navigate("/clients")}
            submitLabel={submitting ? "Kaydediliyor…" : "Oluştur"}
            onSubmit={() => void submit()}
            extraAction={
              submitting
                ? undefined
                : {
                    label: "Oluştur ve Yeni Proje Ekle",
                    onClick: () => void submit(undefined, true),
                  }
            }
            submitting={submitting}
          />
        </div>
      ) : null}
    </div>
  );
}

function Stepper({ step }: { step: Step }): JSX.Element {
  const steps = [
    { n: 1, label: "Şirket" },
    { n: 2, label: "İletişim" },
    { n: 3, label: "Onay" },
  ];
  return (
    <div className="mb-8 flex items-center gap-2">
      {steps.map((s, i) => {
        const active = s.n === step;
        const done = s.n < step;
        return (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: active
                  ? "var(--color-primary)"
                  : done
                    ? "var(--color-success)"
                    : "var(--color-border)",
                color: active || done ? "var(--color-surface)" : "var(--color-text-3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                fontWeight: 600,
                fontFamily: "var(--font-sans)",
                flexShrink: 0,
              }}
            >
              {done ? "✓" : s.n}
            </div>
            <span
              style={{
                fontSize: "0.85rem",
                color: active ? "var(--color-text)" : "var(--color-text-3)",
                fontWeight: active ? 500 : 400,
              }}
            >
              {s.label}
            </span>
            {i < steps.length - 1 ? (
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: done ? "var(--color-success)" : "var(--color-border)",
                  marginLeft: 8,
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
  placeholder,
  required,
  minLength,
  autoFocus,
  listId,
  listOptions,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  autoFocus?: boolean;
  /** `datalist` kimliği — serbest metni kısıtlamaz, yalnız öneri listesi gösterir. */
  listId?: string;
  listOptions?: readonly string[];
}): JSX.Element {
  return (
    <label className="block">
      <span className="block text-sm mb-1.5" style={{ color: "var(--color-text)" }}>
        {label}
      </span>
      <input
        type={type ?? "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        list={listId}
        className="w-full px-3 py-2.5 rounded-lg border"
        style={{
          background: "var(--color-surface)",
          borderColor: "var(--color-border)",
          fontSize: "0.9rem",
        }}
      />
      {listId && listOptions ? (
        <datalist id={listId}>
          {listOptions.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

function Nav({
  canBack,
  canNext,
  onBack,
  onNext,
  onCancel,
  submitLabel,
  onSubmit,
  extraAction,
  submitting,
}: {
  canBack: boolean;
  canNext: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onCancel: () => void;
  submitLabel?: string | undefined;
  onSubmit?: () => void;
  extraAction?: { label: string; onClick: () => void } | undefined;
  submitting?: boolean | undefined;
}): JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-2 mt-4 pt-4"
      style={{ borderTop: "1px solid var(--color-border)" }}
    >
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-2 rounded-lg text-sm"
        style={{
          background: "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-text-3)",
        }}
      >
        İptal
      </button>
      <div className="flex gap-2">
        {canBack ? (
          <button
            type="button"
            onClick={onBack}
            className="px-3 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            ← Geri
          </button>
        ) : null}
        {extraAction ? (
          <button
            type="button"
            onClick={extraAction.onClick}
            disabled={submitting}
            className="px-3 py-2 rounded-lg text-sm disabled:opacity-50"
            style={{
              background: "transparent",
              border: "1px solid var(--color-primary)",
              color: "var(--color-primary)",
            }}
          >
            {extraAction.label}
          </button>
        ) : null}
        {onSubmit ? (
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {submitLabel}
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canNext}
            onClick={onNext}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            İleri →
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ form }: { form: FormData }): JSX.Element {
  const hasContact =
    form.primaryContactName || form.primaryContactEmail || form.primaryContactPhone;
  return (
    <div
      className="rounded-xl p-6 mb-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <h3
        className="mb-4"
        style={{
          fontFamily: "var(--font-display)",
          color: "var(--color-text)",
          fontSize: "1.05rem",
        }}
      >
        Özet
      </h3>
      <div className="grid gap-3">
        <Row label="Şirket" value={form.name} />
        <Row label="Sektör" value={form.sector} />
        <Row label="Ülke" value={form.country} />
        <Row label="Büyüklük" value={form.size} />
        {hasContact ? (
          <>
            <div style={{ borderTop: "1px dashed var(--color-border)", margin: "6px 0" }} />
            <Row label="İletişim Adı" value={form.primaryContactName} />
            <Row label="E-posta" value={form.primaryContactEmail} mono />
            <Row label="Telefon" value={form.primaryContactPhone} mono />
          </>
        ) : (
          <p className="text-sm" style={{ color: "var(--color-text-3)" }}>
            İletişim kişisi girilmedi — sonra düzenleyebilirsiniz.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: "130px 1fr" }}>
      <span style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>{label}</span>
      <span
        style={{
          color: value ? "var(--color-text)" : "var(--color-text-3)",
          fontSize: "0.88rem",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}
